import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const knowledgeDistillationComponent: ComponentDef = {
  id: 'knowledge-distillation',
  name: 'Knowledge Distillation',
  description: 'Train a small student model to match the soft targets of a large teacher model.',
  category: 'training',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'student_dim', label: 'Student dimension', type: 'number', default: 256, min: 64, max: 4096, step: 64 },
    {
      name: 'variant',
      label: 'KD Method',
      type: 'select',
      default: 'logit_kd',
      options: [
        { label: 'Logit KD (Hinton)', value: 'logit_kd' },
        { label: 'Feature KD', value: 'feature_kd' },
        { label: 'Online KD', value: 'online_kd' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const student_dim = Number(params.student_dim);
    const variant = String(params.variant || 'logit_kd');

    const g = new GraphBuilder();

    if (variant === 'logit_kd') {
      // Logit KD (Hinton): match softened output distributions from teacher logits
      g.input('x', [seq_len, d_model]);

      // Teacher (frozen)
      g.linear('teacher', 'x', [d_model, d_model]);
      g.scale('teacher_logits_T', 'teacher', '÷ T (temperature)');
      g.softmax('soft_targets', 'teacher_logits_T', -1);

      // Student
      g.linear('student', 'x', [d_model, student_dim]);
      g.scale('student_logits_T', 'student', '÷ T (temperature)');
      g.softmax('soft_student', 'student_logits_T', -1);

      // KL divergence between softened distributions
      g.loss('kd_loss', 'soft_student', 'soft_targets');
      g.scale('scaled_loss', 'kd_loss', 'T² · KL  [Hinton KD loss]');
      g.output('out', 'scaled_loss');

    } else if (variant === 'feature_kd') {
      // Feature KD: match intermediate representations (hint layers)
      g.input('x', [seq_len, d_model]);

      // Teacher intermediate features
      g.linear('teacher_layer1', 'x', [d_model, d_model]);
      g.linear('teacher_layer2', 'teacher_layer1', [d_model, d_model]);
      g.norm('teacher_features', 'teacher_layer2', 'Teacher Features');

      // Student intermediate features (smaller dim)
      g.linear('student_layer1', 'x', [d_model, student_dim]);
      g.linear('student_layer2', 'student_layer1', [student_dim, student_dim]);
      g.norm('student_features', 'student_layer2', 'Student Features');

      // Project student to teacher dimension for comparison
      g.linear('projector', 'student_features', [student_dim, d_model]);

      // MSE loss between teacher and projected student features
      g.loss('feature_loss', 'projector', 'teacher_features');
      g.scale('kd_loss', 'feature_loss', 'Feature Matching Loss');
      g.output('out', 'kd_loss');

    } else {
      // Online KD: co-training teacher and student — teacher also learns from data
      g.input('x', [seq_len, d_model]);
      g.input('labels', [seq_len]);

      // Teacher (also trained, not frozen)
      g.linear('teacher', 'x', [d_model, d_model]);
      g.linear('teacher_logits', 'teacher', [d_model, d_model]);
      g.loss('teacher_ce', 'teacher_logits', 'labels');

      // Student
      g.linear('student', 'x', [d_model, student_dim]);
      g.linear('student_logits', 'student', [student_dim, d_model]);
      g.loss('student_ce', 'student_logits', 'labels');

      // KD loss: student matches teacher soft targets
      g.softmax('teacher_soft', 'teacher_logits', -1);
      g.softmax('student_soft', 'student_logits', -1);
      g.loss('kd_loss', 'student_soft', 'teacher_soft');

      // Combined: teacher CE + student CE + α·KD loss
      g.add('teacher_total', 'teacher_ce', 'kd_loss');
      g.add('total_loss', 'teacher_total', 'student_ce');
      g.scale('online_loss', 'total_loss', 'Online KD Loss');
      g.output('out', 'online_loss');
    }

    return g.build();
  }
};
